// src/pages/LandingPage.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

import Seo from '../components/Seo';
import Banner from '../components/Banner';
import Icons_Section from '../components/Icons_Section';
import Developer_Section from '../components/Developer_Section';
// import Video_Section from '../components/Video_Section';
import Social_Section from '../components/Social_Section';
import Form_Section from '../components/Form_Section';
import Footer from '../components/Footer';

const SECTIONS = [
  { id: 'banner', steps: 1 },
  { id: 'Section1', steps: 5 },
  { id: 'Section2', steps: 1 },
  { id: 'Section3', steps: 1 },
  { id: 'Section4', steps: 1 },
  { id: 'Section5', steps: 1 },
];

export default function LandingPage() {
  // basic state
  const [isMobile, setIsMobile] = useState(false);

  // refs & state for mobile section scroll logic
  const containerRef = useRef(null);
  const sectionRefs = useRef([]); // filled with DOM nodes for each section
  const [sectionIndex, setSectionIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [isInSectionV1, setIsInSectionV1] = useState(false);
  const [isSectionV1Visible, setIsSectionV1Visible] = useState(false);
  const [shouldForceScroll, setShouldForceScroll] = useState(false);

  // helpers for touch/wheel locking
  const scrollLock = useRef(false);
  const touchStartYRef = useRef(0);
  const isTouchingRef = useRef(false);
  const prevSectionIndex = useRef(sectionIndex);
  const lastScrollTimeRef = useRef(0);
  const forceScrollTimeoutRef = useRef(null);

  // keep prevSectionIndex up to date
  useEffect(() => {
    prevSectionIndex.current = sectionIndex;
  }, [sectionIndex]);

  // detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // background zoom effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      const progress = window.scrollY;
      const isMobileView = window.innerWidth < 640;
      const firstPoint = 698;
      const secondPoint = 1610.7;
      const finalPoint = 2140.7;
      let MAX_ZOOM_PHASE_1 = 1.65;
      let MAX_ZOOM_PHASE_2 = 1.75;
      let MAX_ZOOM_PHASE_3 = 1.42;
      if (isMobileView) {
        MAX_ZOOM_PHASE_1 += 0.2;
        MAX_ZOOM_PHASE_2 += 0.3;
        MAX_ZOOM_PHASE_3 += 0.5;
      }
      let scale = 1;

      if (progress <= firstPoint) {
        const p = progress / firstPoint;
        scale = 1 + p * (MAX_ZOOM_PHASE_1 - 1);
      } else if (progress > firstPoint && progress < secondPoint) {
        const p = (progress - firstPoint) / (secondPoint - firstPoint);
        scale = MAX_ZOOM_PHASE_2 - p * (MAX_ZOOM_PHASE_2 - 1);
        if (scale > MAX_ZOOM_PHASE_2) scale = MAX_ZOOM_PHASE_2;
      } else if (progress > secondPoint && progress <= finalPoint) {
        const p = (progress - secondPoint) / (finalPoint - secondPoint);
        scale = 1 + p * (MAX_ZOOM_PHASE_3 - 1);
      } else {
        scale = MAX_ZOOM_PHASE_3;
      }

      document.querySelectorAll('.bg-zoom').forEach((el) => {
        try {
          // defensive: element might be null or not HTMLElement
          el.style.transform = `scale(${scale})`;
        } catch (err) {
          // ignore
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // keep section state in sync when section V1 visibility changes
  useEffect(() => {
    if (!isSectionV1Visible && stepIndex !== 0) {
      setStepIndex(0);
      setIsInSectionV1(false);
      setShouldForceScroll(true);
    }
  }, [isSectionV1Visible, stepIndex]);
  useEffect(() => {
    if (shouldForceScroll && isSectionV1Visible && stepIndex === 0) {
      const sectionV1 = sectionRefs.current[1];
      if (sectionV1) {
        // small delay to ensure layout stable
        setTimeout(() => {
          window.scrollTo({
            top: sectionV1.offsetTop,
            behavior: 'auto',
          });
          setIsInSectionV1(true);
          setShouldForceScroll(false);
        }, 100);
      }
    }
  }, [shouldForceScroll, isSectionV1Visible, stepIndex]);
  // ensure when navigating programmatically to section 1 on mobile we set flags
  useEffect(() => {
    if (!isMobile) return;
    const prev = prevSectionIndex.current;
    // emulate your original logic: when moving into section 1 from above/below
    if ((prev === 0 && sectionIndex === 1) || (prev === 2 && sectionIndex === 1)) {
      setIsInSectionV1(true);
      setStepIndex(0);
      setShouldForceScroll(true);
    }
  }, [sectionIndex, isMobile]);
  // goNext / goPrev (with simple debounce)
  const goNext = useCallback(() => {
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 500) return;
    lastScrollTimeRef.current = now;
    if (isInSectionV1 && isSectionV1Visible) {
      const total = SECTIONS[1].steps;
      if (stepIndex < total - 1) {
        setStepIndex((s) => s + 1);
        return;
      } else {
        if (sectionIndex < SECTIONS.length - 1) {
          const next = sectionIndex + 1;
          setSectionIndex(next);
          setIsInSectionV1(false);
          setStepIndex(0);
          setTimeout(() => {
            sectionRefs.current[next]?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    } else {
      if (sectionIndex < SECTIONS.length - 1) {
        const next = sectionIndex + 1;
        setSectionIndex(next);
        setStepIndex(0);
        sectionRefs.current[next]?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [sectionIndex, stepIndex, isInSectionV1, isSectionV1Visible]);
  const goPrev = useCallback(() => {
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 500) return;
    lastScrollTimeRef.current = now;
    if (isInSectionV1 && isSectionV1Visible) {
      if (stepIndex > 0) {
        setStepIndex((s) => s - 1);
        return;
      } else {
        if (sectionIndex > 0) {
          const prev = sectionIndex - 1;
          setSectionIndex(prev);
          setIsInSectionV1(false);
          setStepIndex(0);
          setTimeout(() => {
            sectionRefs.current[prev]?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    } else {
      if (sectionIndex > 0) {
        const prev = sectionIndex - 1;
        setSectionIndex(prev);
        if (prev === 1) {
          setIsInSectionV1(true);
          setStepIndex(0);
          setShouldForceScroll(true);
        } else {
          setStepIndex(0);
        }
        sectionRefs.current[prev]?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [sectionIndex, stepIndex, isInSectionV1, isSectionV1Visible]);
  // MOBILE SCROLL + TOUCH LOCK SYSTEM (keeps behaviour you wrote)
  useEffect(() => {
    if (!isMobile) return;
    const sectionV1 = sectionRefs.current[1];
    if (!sectionV1) return;
    const SWIPE_THRESHOLD = 60;
    const TOP_TOLERANCE = 10;
    const checkIsAtTop = () => {
      const rect = sectionV1.getBoundingClientRect();
      const isAtTop = rect.top >= -TOP_TOLERANCE && rect.top <= TOP_TOLERANCE;
      return isAtTop;
    };
    const onWheel = (e) => {
      const now = Date.now();
      if (now - lastScrollTimeRef.current < 500) {
        e.preventDefault();
        return;
      }
      const isAtTop = checkIsAtTop();
      if (isInSectionV1 && isSectionV1Visible && isAtTop) {
        if (e.cancelable) e.preventDefault();
        if (scrollLock.current) return;
        scrollLock.current = true;
        e.deltaY > 0 ? goNext() : goPrev();
        setTimeout(() => {
          scrollLock.current = false;
        }, 600);
      }
    };
    const onTouchStart = (e) => {
      if (!isInSectionV1 || !isSectionV1Visible) return;
      const isAtTop = checkIsAtTop();
      if (!isAtTop) return;
      touchStartYRef.current = e.touches[0].clientY;
      isTouchingRef.current = true;
      e.stopPropagation();
    };
    const onTouchMove = (e) => {
      if (!isTouchingRef.current || !isInSectionV1 || !isSectionV1Visible) return;
      const isAtTop = checkIsAtTop();
      if (!isAtTop) return;
      if (e.cancelable) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onTouchEnd = (e) => {
      if (!isTouchingRef.current) return;
      isTouchingRef.current = false;
      if (!isInSectionV1 || !isSectionV1Visible) return;
      const isAtTop = checkIsAtTop();
      if (!isAtTop) return;
      const endY = e.changedTouches[0].clientY;
      const diff = touchStartYRef.current - endY;
      if (Math.abs(diff) < SWIPE_THRESHOLD) return;
      if (scrollLock.current) return;
      scrollLock.current = true;
      diff > 0 ? goNext() : goPrev();
      setTimeout(() => {
        scrollLock.current = false;
      }, 600);
    };
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.target === sectionV1) {
          const isVisible = entry.isIntersecting && entry.intersectionRatio > 0.8;
          const wasVisible = isSectionV1Visible;
          setIsSectionV1Visible(isVisible);
          if (isVisible && !wasVisible) {
            if (sectionIndex !== 1) {
              setSectionIndex(1);
              setIsInSectionV1(true);
              setStepIndex(0);
              if (forceScrollTimeoutRef.current) {
                clearTimeout(forceScrollTimeoutRef.current);
              }
              forceScrollTimeoutRef.current = setTimeout(() => {}, 0);
            } else if (stepIndex !== 0) {
              setStepIndex(0);
              setShouldForceScroll(true);
            }
          }
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, {
      threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9],
      rootMargin: '0px',
    });
    observer.observe(sectionV1);
    const ensureAtTop = () => {
      if (isInSectionV1 && isSectionV1Visible && !checkIsAtTop()) {
        sectionV1.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    };
    const topCheckInterval = setInterval(ensureAtTop, 100);
    const wheelOptions = { passive: false, capture: true };
    const touchOptions = { passive: false };
    const touchMoveOptions = { passive: false, capture: true };
    document.addEventListener('wheel', onWheel, wheelOptions);
    document.addEventListener('touchstart', onTouchStart, touchOptions);
    document.addEventListener('touchmove', onTouchMove, touchMoveOptions);
    document.addEventListener('touchend', onTouchEnd, touchOptions);
    return () => {
      observer.disconnect();
      clearInterval(topCheckInterval);
      if (forceScrollTimeoutRef.current) {
        clearTimeout(forceScrollTimeoutRef.current);
      }
      document.removeEventListener('wheel', onWheel, wheelOptions);
      document.removeEventListener('touchstart', onTouchStart, touchOptions);
      document.removeEventListener('touchmove', onTouchMove, touchMoveOptions);
      document.removeEventListener('touchend', onTouchEnd, touchOptions);
    };
  }, [isMobile, isInSectionV1, isSectionV1Visible, goNext, goPrev, sectionIndex, stepIndex]);
  // styles
  const styles = {
    desktopBg: {
      position: 'fixed',
      inset: 0,
      backgroundImage: "url('/bgbanner.jpg')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      zIndex: -10,
    },
    mobileBg: {
      position: 'fixed',
      inset: 0,
      backgroundImage: "url('/mobbg.png')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      zIndex: -10,
      display: 'block',
    },
    relativeContainer: {
      position: 'relative',
      zIndex: 10,
    },
    mobileContainer: {
      width: '100%',
      minHeight: '100vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      scrollBehavior: 'smooth',
    },
    minHeightSection: {
      minHeight: '100vh',
      position: 'relative',
      zIndex: 40,
    },
  };
  // DESKTOP LAYOUT
  const DesktopLayout = (
    <div>
      <div className="bg-zoom" style={styles.desktopBg} />
      <div style={styles.relativeContainer}>
        <div ref={(el) => (sectionRefs.current[0] = el)}>
          <Banner />
        </div>
        {/* NOTE: on desktop we render SectionV1..V5 directly (your original did too) */}
        <div ref={(el) => (sectionRefs.current[1] = el)}>
          <Icons_Section activeStep={stepIndex} />
        </div>
        <div ref={(el) => (sectionRefs.current[2] = el)}>
          <Developer_Section />
        </div>
        {/* <div ref={(el) => (sectionRefs.current[3] = el)}>
          <Video_Section />
        </div> */}
        <div ref={(el) => (sectionRefs.current[4] = el)}>
          <Social_Section />
        </div>
        <div ref={(el) => (sectionRefs.current[5] = el)}>
          <Form_Section />
        </div>
        <Footer />
      </div>
    </div>
  );
  // MOBILE LAYOUT
  const MobileLayout = (
    <div ref={containerRef} style={styles.mobileContainer}>
      <div className="bg-zoom" style={styles.mobileBg} />
      <div ref={(el) => (sectionRefs.current[0] = el)}>
        <Banner />
      </div>
      <div
        ref={(el) => {
          sectionRefs.current[1] = el;
        }}
      >
        <Icons_Section activeStep={isInSectionV1 && isSectionV1Visible ? stepIndex : 0} />
      </div>
      <div ref={(el) => (sectionRefs.current[2] = el)} style={styles.minHeightSection}>
        <Developer_Section />
      </div>
      {/* <div ref={(el) => (sectionRefs.current[3] = el)} style={styles.minHeightSection}>
        <Video_Section />
      </div> */}
      <div ref={(el) => (sectionRefs.current[4] = el)} style={styles.minHeightSection}>
        <Social_Section />
      </div>
      <div ref={(el) => (sectionRefs.current[5] = el)} style={styles.minHeightSection}>
        <Form_Section />
      </div>
      <Footer />
    </div>
  );
  return (
    <Box>
      <Seo
        path="/"
        title=""
        description="BingePlay is the TikTok for games. A swipeable feed of playable game demos. Try real mobile games instantly. Download only what you love."
      />
      {isMobile ? MobileLayout : DesktopLayout}
    </Box>
  );
}
